import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, PencilSimple, CaretUp, CaretDown, DotsSixVertical, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";

const FIELD_TYPES = ["text", "textarea", "number", "date", "datetime", "select", "radio", "checkbox", "toggle", "email", "phone", "url"];
const EMPTY_FORM = {
  slug: "", name: "", description: "", enabled: true,
  submit_label: "Submit", success_message: "Thanks! Your response has been recorded.",
  fields: [],
};

function slugify(s) { return (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, ""); }

function FieldEditor({ field, onChange, onRemove, allFields, index, onMove }) {
  const upd = (patch) => onChange(Object.assign({}, field, patch));
  const validation = field.validation || {};
  const setVal = (patch) => upd({ validation: Object.assign({}, validation, patch) });
  const vif = field.visible_if || {};
  const setVif = (patch) => upd({ visible_if: Object.assign({}, vif, patch) });
  const controllers = allFields.filter((f) => f.key && f.key !== field.key);
  const isNumeric = field.type === "number";
  const isStr = ["text", "textarea", "email", "phone", "url"].includes(field.type);
  const isChoice = ["select", "radio", "checkbox"].includes(field.type);
  return (
    <div className="border rounded-md p-3 space-y-3 bg-secondary/30" data-testid={"form-field-" + index}>
      <div className="grid grid-cols-12 gap-2">
        <div className="col-span-1 flex flex-col items-center gap-1 pt-1">
          <DotsSixVertical size={12} className="text-muted-foreground" />
          <button onClick={() => onMove(index, -1)} disabled={index === 0} className="disabled:opacity-30"><CaretUp size={12} /></button>
          <button onClick={() => onMove(index, 1)} disabled={index === allFields.length - 1} className="disabled:opacity-30"><CaretDown size={12} /></button>
        </div>
        <div className="col-span-10 space-y-2">
          <div className="grid grid-cols-12 gap-2">
            <Input className="col-span-4 h-8" placeholder="Label" value={field.label}
              onChange={(e) => upd({ label: e.target.value, key: field.key || slugify(e.target.value) })} />
            <Input className="col-span-3 h-8 font-mono text-xs" placeholder="key" value={field.key}
              onChange={(e) => upd({ key: slugify(e.target.value) })} />
            <Select value={field.type} onValueChange={(v) => upd({ type: v })}>
              <SelectTrigger className="col-span-3 h-8"><SelectValue /></SelectTrigger>
              <SelectContent>{FIELD_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <label className="col-span-2 flex items-center gap-2 text-xs">
              <Switch checked={field.required} onCheckedChange={(v) => upd({ required: v })} />Required
            </label>
          </div>
          <Input className="h-8" placeholder="Placeholder / help text"
            value={field.placeholder} onChange={(e) => upd({ placeholder: e.target.value })} />
          {isChoice && (
            <Input className="h-8" placeholder="Options (comma separated)"
              value={(field.options || []).join(", ")}
              onChange={(e) => upd({ options: e.target.value.split(",").map((s) => s.trim()).filter(Boolean) })}
              data-testid={"form-field-options-" + index} />
          )}
          <details className="text-xs">
            <summary className="cursor-pointer text-muted-foreground">Validation & conditional visibility</summary>
            <div className="mt-2 space-y-2 pl-2 border-l-2 border-primary/30">
              {isNumeric && (
                <div className="grid grid-cols-2 gap-2">
                  <div><Label className="text-[10px]">MIN</Label><Input className="h-7" type="number" value={validation.min ?? ""} onChange={(e) => setVal({ min: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                  <div><Label className="text-[10px]">MAX</Label><Input className="h-7" type="number" value={validation.max ?? ""} onChange={(e) => setVal({ max: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                </div>
              )}
              {isStr && (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label className="text-[10px]">MIN LEN</Label><Input className="h-7" type="number" value={validation.min_length ?? ""} onChange={(e) => setVal({ min_length: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                    <div><Label className="text-[10px]">MAX LEN</Label><Input className="h-7" type="number" value={validation.max_length ?? ""} onChange={(e) => setVal({ max_length: e.target.value === "" ? null : Number(e.target.value) })} /></div>
                  </div>
                  <div><Label className="text-[10px]">REGEX PATTERN</Label>
                    <Input className="h-7 font-mono" placeholder="^[A-Z]{2,}$" value={validation.pattern || ""} onChange={(e) => setVal({ pattern: e.target.value })} />
                  </div>
                </>
              )}
              <div><Label className="text-[10px]">CUSTOM ERROR MESSAGE</Label>
                <Input className="h-7" value={validation.message || ""} onChange={(e) => setVal({ message: e.target.value })} placeholder="Please enter a valid value" />
              </div>
              <div className="pt-1">
                <Label className="text-[10px]">SHOW ONLY WHEN</Label>
                <div className="grid grid-cols-12 gap-2 mt-1">
                  <Select value={vif.field || "__none__"} onValueChange={(v) => setVif({ field: v === "__none__" ? null : v })}>
                    <SelectTrigger className="col-span-5 h-7"><SelectValue placeholder="Always visible" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">— Always visible —</SelectItem>
                      {controllers.map((c) => <SelectItem key={c.key} value={c.key}>{c.label} ({c.key})</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <span className="col-span-2 text-center text-muted-foreground pt-1">equals</span>
                  <Input className="col-span-5 h-7" value={vif.equals ?? ""} onChange={(e) => setVif({ equals: e.target.value })} placeholder="value" disabled={!vif.field} />
                </div>
              </div>
            </div>
          </details>
        </div>
        <button className="col-span-1 text-destructive" onClick={onRemove} data-testid={"form-field-delete-" + index}>
          <Trash size={14} className="mx-auto" />
        </button>
      </div>
    </div>
  );
}

function FormDialog({ open, onOpenChange, initial, onSave }) {
  const [f, setF] = useState(EMPTY_FORM);
  useEffect(() => { setF(initial ? Object.assign({}, EMPTY_FORM, initial) : EMPTY_FORM); }, [initial, open]);
  const addField = () => setF(Object.assign({}, f, {
    fields: (f.fields || []).concat([{ key: "", label: "", type: "text", required: false, options: [], placeholder: "" }]),
  }));
  const updField = (i, patch) => setF(Object.assign({}, f, {
    fields: f.fields.map((x, idx) => idx === i ? patch : x),
  }));
  const rmField = (i) => setF(Object.assign({}, f, {
    fields: f.fields.filter((_, idx) => idx !== i),
  }));
  const moveField = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= f.fields.length) return;
    const next = f.fields.slice();
    const tmp = next[i]; next[i] = next[j]; next[j] = tmp;
    setF(Object.assign({}, f, { fields: next }));
  };
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Name required");
    const slug = f.slug || slugify(f.name);
    if (!slug) return toast.error("Slug required");
    try { await onSave(Object.assign({}, f, { slug })); onOpenChange(false); }
    catch (e) { toast.error((e && e.response && e.response.data && e.response.data.detail) || "Save failed"); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="form-dialog">
        <DialogHeader><DialogTitle>{initial && initial.id ? "Edit form" : "New form"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">NAME</Label>
              <Input value={f.name} onChange={(e) => setF(Object.assign({}, f, { name: e.target.value, slug: f.slug || slugify(e.target.value) }))} data-testid="form-name" /></div>
            <div><Label className="text-xs">SLUG</Label>
              <Input className="font-mono" value={f.slug} onChange={(e) => setF(Object.assign({}, f, { slug: slugify(e.target.value) }))} data-testid="form-slug" /></div>
          </div>
          <div><Label className="text-xs">DESCRIPTION</Label>
            <Textarea rows={2} value={f.description} onChange={(e) => setF(Object.assign({}, f, { description: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">SUBMIT BUTTON</Label>
              <Input value={f.submit_label} onChange={(e) => setF(Object.assign({}, f, { submit_label: e.target.value }))} /></div>
            <div><Label className="text-xs">SUCCESS MESSAGE</Label>
              <Input value={f.success_message} onChange={(e) => setF(Object.assign({}, f, { success_message: e.target.value }))} /></div>
          </div>
          <div className="pt-2 border-t">
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs">FIELDS · {f.fields.length}</Label>
              <Button size="sm" variant="outline" onClick={addField} data-testid="form-add-field"><Plus size={12} className="mr-1" />Field</Button>
            </div>
            <div className="space-y-2">
              {f.fields.map((fld, i) => (
                <FieldEditor key={i} field={fld} index={i} allFields={f.fields}
                  onChange={(p) => updField(i, p)} onRemove={() => rmField(i)} onMove={moveField} />
              ))}
              {!f.fields.length && <div className="text-center text-xs text-muted-foreground py-6">No fields yet — click “Field” to add one.</div>}
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={f.enabled} onCheckedChange={(v) => setF(Object.assign({}, f, { enabled: v }))} data-testid="form-enabled" />
            Enabled
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} data-testid="form-save">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function FormBuilder() {
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/platform/forms"); setForms(r.data); }
    catch { toast.error("Failed to load forms"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (payload) => {
    if (editing && editing.id) {
      await api.put("/platform/forms/" + editing.id, payload);
      toast.success("Form updated");
    } else {
      await api.post("/platform/forms", payload);
      toast.success("Form created");
    }
    load();
  };
  const remove = async (form) => {
    const cnt = form.submission_count || 0;
    const ok = cnt
      ? window.confirm(`This form has ${cnt} submission(s). Purge and delete?`)
      : window.confirm(`Delete “${form.name}”?`);
    if (!ok) return;
    try {
      const url = "/platform/forms/" + form.id + (cnt ? "?force=true" : "");
      await api.delete(url); toast.success("Deleted"); load();
    } catch (e) { toast.error("Delete failed"); }
  };

  return (
    <Card className="p-6" data-testid="form-builder">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="overline text-muted-foreground">Data capture</div>
          <h3 className="font-heading font-bold text-xl">Form Builder</h3>
          <p className="text-xs text-muted-foreground mt-1">Design dynamic forms with validation and conditional fields. Tenants fill them at <span className="font-mono">/f/&lt;slug&gt;</span>.</p>
        </div>
        <Button size="sm" onClick={() => { setEditing(null); setDialogOpen(true); }} data-testid="form-add">
          <Plus size={14} className="mr-1.5" />New form
        </Button>
      </div>
      {loading ? (
        <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {forms.map((form) => (
            <div key={form.id} className={"border rounded-lg p-4 space-y-3 " + (form.enabled ? "" : "opacity-60")} data-testid={"form-card-" + form.slug}>
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-9 h-9 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    <ClipboardText size={18} weight="duotone" className="text-primary" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-bold text-sm">{form.name}
                      {!form.enabled && <span className="ml-2 text-[9px] uppercase tracking-wider text-muted-foreground border px-1 rounded">disabled</span>}
                    </div>
                    <div className="text-[11px] text-muted-foreground font-mono">/f/{form.slug} · {form.fields?.length || 0} fields · {form.submission_count || 0} submissions</div>
                    {form.description && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{form.description}</div>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 justify-end">
                <button onClick={() => { setEditing(form); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-primary" title="Edit" data-testid={"form-edit-" + form.slug}>
                  <PencilSimple size={13} weight="duotone" />
                </button>
                <button onClick={() => remove(form)} className="p-1.5 rounded hover:bg-destructive/10 text-muted-foreground hover:text-destructive" title="Delete" data-testid={"form-delete-" + form.slug}>
                  <Trash size={13} weight="duotone" />
                </button>
              </div>
            </div>
          ))}
          {!forms.length && (
            <div className="col-span-2 py-12 text-center text-muted-foreground text-sm">
              <ClipboardText size={28} weight="duotone" className="mx-auto mb-2 opacity-50" />
              No forms yet — click “New form”.
            </div>
          )}
        </div>
      )}
      <FormDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSave={save} />
    </Card>
  );
}
